import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBandStore } from "../../stores/bandStore.js";
import { Button } from "../../ui/Button.js";
import { LpcAvatar } from "./LpcAvatar.js";
import { AvatarFrame } from "./AvatarFrame.js";
import { BandCostBadge } from "./BandCostBadge.js";
import { DeleteBandModal } from "./DeleteBandModal.js";
import type { Band } from "../../infra/api.js";

function catalogNumber(_b: Band, index: number): string {
  return `MS·${String(index + 1).padStart(3, "0")}`;
}

function BandCard({
  band,
  index,
  onDelete,
}: {
  band: Band;
  index: number;
  onDelete: (band: Band) => void;
}): JSX.Element {
  return (
    <Link to={`/bands/${band.id}`} className="group block">
      <article className="card-hover relative flex h-full flex-col gap-4 border border-border bg-tape p-5 shadow-sleeve">
        <div className="flex items-center justify-between">
          <span className="catalog-num">{catalogNumber(band, index)}</span>
          <div className="flex items-center gap-2">
            <BandCostBadge bandId={band.id} />
            <span className="catalog-num text-ink-dim">
              {new Date(band.created_at).getFullYear()}
            </span>
          </div>
        </div>

        <h2
          className="font-display text-[28px] leading-[0.95] tracking-tight text-paper transition-colors group-hover:text-butter"
          style={{ fontVariationSettings: '"opsz" 96, "wght" 600, "SOFT" 80, "WONK" 1' }}
        >
          {band.name}
        </h2>

        <div className="flex flex-wrap items-end gap-2">
          {band.slots.length === 0 ? (
            <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
              sem elenco
            </span>
          ) : (
            band.slots.map((s) => (
              <div key={s.slot_role} className="flex flex-col items-center gap-1">
                <AvatarFrame size={44}>
                  <LpcAvatar
                    layers={s.artist.lpc_layers}
                    size={44}
                    label={s.artist.name}
                    instrument={s.slot_role}
                  />
                </AvatarFrame>
                <span className="font-mono text-[8px] uppercase tracking-widest2 text-ink-dim">
                  {s.slot_role}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="tape-rule mt-auto" />

        <dl className="grid grid-cols-2 gap-y-1 font-mono text-[10px] uppercase tracking-widest2">
          <dt className="text-ink-dim">faixas</dt>
          <dd className="text-right text-paper">
            <span className="tabular-nums">{String(band.song_count).padStart(2, "0")}</span>
          </dd>
          <dt className="text-ink-dim">elenco</dt>
          <dd className="text-right text-paper">
            <span className="tabular-nums">{String(band.slots.length).padStart(2, "0")}</span> / 05
          </dd>
        </dl>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(band);
            }}
            aria-label={`apagar banda ${band.name}`}
            className="font-mono text-[9px] uppercase tracking-widest2 text-ink-dim transition-colors hover:text-vinyl"
          >
            apagar
          </button>
        </div>
      </article>
    </Link>
  );
}

function BandSkeleton(): JSX.Element {
  return (
    <div className="border border-border bg-tape p-5 shadow-sleeve">
      <div className="skeleton mb-3 h-3 w-16" />
      <div className="skeleton mb-4 h-8 w-2/3" />
      <div className="flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="skeleton h-12 w-12" />
        ))}
      </div>
    </div>
  );
}

export function BandsList(): JSX.Element {
  const bands = useBandStore((s) => s.bands);
  const loading = useBandStore((s) => s.loading);
  const error = useBandStore((s) => s.error);
  const [deleteTarget, setDeleteTarget] = useState<Band | null>(null);

  useEffect(() => {
    void useBandStore.getState().fetch();
  }, []);

  async function onConfirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    await useBandStore.getState().remove(deleteTarget.id);
    setDeleteTarget(null);
  }

  return (
    <div className="flex flex-col gap-10">
      <section className="reveal grid grid-cols-12 gap-6 border-b border-border pb-10">
        <div className="col-span-12 md:col-span-8">
          <p className="catalog-num mb-3">CATÁLOGO / ROSTER</p>
          <h1
            className="font-display text-[56px] leading-[0.9] tracking-tight text-paper md:text-[80px]"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            As bandas <span className="italic text-vinyl">do selo</span>
          </h1>
          <p className="mt-4 max-w-prose text-sm leading-relaxed text-ink-muted">
            Cada banda é um time pequeno de personagens com voz própria. Crie, ensaia, grava — o
            palco vem nas próximas fases.
          </p>
        </div>
        <div className="col-span-12 flex items-end justify-end md:col-span-4">
          <Link to="/bands/new">
            <Button variant="primary">+ Formar banda</Button>
          </Link>
        </div>
      </section>

      {error && (
        <p className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl">
          Erro: {error}
        </p>
      )}

      {loading && bands.length === 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <BandSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && bands.length === 0 && (
        <div className="reveal-1 flex flex-col items-center gap-4 border border-dashed border-border p-12 text-center">
          <p className="font-display text-2xl text-paper md:text-3xl">Sem bandas ainda.</p>
          <p className="font-mono text-xs uppercase tracking-widest2 text-ink-muted">
            Cadastre artistas primeiro, depois forme o primeiro grupo.
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            <Link to="/artists/new">
              <Button variant="secondary">+ Novo personagem</Button>
            </Link>
            <Link to="/bands/new">
              <Button variant="primary">+ Criar primeira banda</Button>
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {bands.map((b, i) => (
          <div key={b.id} className={`reveal reveal-${(i % 5) + 1}`}>
            <BandCard band={b} index={i} onDelete={setDeleteTarget} />
          </div>
        ))}
      </div>

      {deleteTarget && (
        <DeleteBandModal
          open
          bandId={deleteTarget.id}
          bandName={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void onConfirmDelete()}
        />
      )}
    </div>
  );
}
